import React, { useState, useRef, useEffect } from "react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Play, ArrowRight, Star } from "lucide-react";
import { motion, useAnimation } from "framer-motion";
import shopifyLogo from "../../Assets/Images/shopify-logo.png";
import shopifyDemoVid from "../../Assets/Videos/Shopify Demo Vid.mp4";

const HeroSection = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const controls = useAnimation();
  const sectionRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      // Trigger when the section is scrolled up by 100px or more
      if (rect.top < -100) {
        setShowVideo(true);
        controls.start({
          y: "-120px",
          scale: 0.85,
          transition: { type: "spring", stiffness: 80, damping: 20 },
        });
      } else {
        setShowVideo(false);
        controls.start({
          y: 0,
          scale: 1,
          transition: { type: "spring", stiffness: 80, damping: 20 },
        });
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [controls]);

  useEffect(() => {
    if (showVideo) {
      setIsPlaying(true);
    }
  }, [showVideo]);

  return (
    <section
      ref={sectionRef}
      className="w-full min-h-screen py-12 md:py-24 lg:py-32 xl:py-72 relative overflow-hidden flex items-center justify-center bg-gradient-to-br from-red-50 via-orange-50 to-red-50"
    >
      {/* Shopify logo background image, hidden but visible as a watermark */}
      <img
        src={shopifyLogo}
        alt="Shopify Logo Background"
        className="pointer-events-none select-none opacity-10 absolute left-1/2 top-1/2 w-[600px] max-w-none -translate-x-1/2 -translate-y-1/2 z-0"
        aria-hidden="true"
      />
      <div className="container px-4 md:px-6 relative z-10 flex flex-col items-center justify-center">
        <motion.div
          animate={controls}
          initial={{ y: 0, scale: 1 }}
          className="flex flex-col items-center space-y-4 text-center justify-center"
          style={{ willChange: "transform" }}
        >
          <Badge className=" bg-red-100 text-red-800 hover:bg-red-200">
            {/* <Star className="w-4 h-4 mr-1" /> */}
            Shopify App
          </Badge>
          <h1 className="font-open-sans text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl lg:text-6xl/none bg-clip-text text-transparent bg-gradient-to-tl from-red-200 via-red-600 to-red-200 mb-6 leading-tight">
            Reach new customers for your
            <span className="text-[#96bf48] block">Shopify Store</span>
          </h1>
          {/* <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            Join 50,000+ merchants who've increased their sales by 40% with our
            powerful automation tools. Set up in minutes, see results in days.
          </p> */}
          {/* <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              size="lg"
              className="bg-red-600 hover:bg-red-700 text-grey-500 font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Install Free App
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              variant="outline"
              size="lg"
              className=" font-semibold rounded-lg border-2 hover:bg-gray-50"
            >
              <Play className="mr-2 w-5 h-5" />
              Watch Demo (2 min)
            </Button>
          </div> */}
          <p className="text-sm text-gray-500 mt-4">
            ✓ No setup fees • ✓ Cancel anytime
          </p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={showVideo ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="relative max-w-4xl mx-auto w-full mt-8"
          style={{ pointerEvents: showVideo ? "auto" : "none" }}
        >
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                <div className="w-3 h-3 bg-green-400 rounded-full"></div>
              </div>
            </div>
            <div className="aspect-video bg-gradient-to-br from-red-100 to-orange-100 flex items-center justify-center relative">
              {isPlaying ? (
                <video
                  src={shopifyDemoVid}
                  controls
                  autoPlay
                  muted
                  className="w-full h-full object-contain rounded-b-2xl"
                />
              ) : (
                <button
                  className="absolute inset-0 flex flex-col items-center justify-center w-full h-full focus:outline-none"
                  onClick={() => setIsPlaying(true)}
                  aria-label="Play demo video"
                >
                  <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg hover:scale-105 transition-transform">
                    <Play className="w-8 h-8 text-white ml-1" />
                  </div>
                  <p className="text-gray-600 font-medium">
                    Watch Our 1-min Demo
                  </p>
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
